import {
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { useCallback, useEffect, useState } from "react";
import type { PairingCode } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import { useFormat } from "../i18n/format";
import type { Translate } from "../i18n/translate";
import { errorText } from "../utils/apiError";

interface Props {
  premiseId: string;
  onCreateClick: () => void;
  /** Bumped by the parent after a successful create to trigger a refresh. */
  refreshTick?: number;
}

type CodeStatus = "active" | "expired";

function statusOf(p: PairingCode): CodeStatus {
  if (Date.parse(p.expiresAt) < Date.now()) return "expired";
  return "active";
}

function timeLeft(t: Translate, iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return t("premises:installationCodesView.status.expired");
  const mins = Math.floor(ms / 60_000);
  if (mins < 60)
    return t("premises:devicePresentation.duration.minutes", { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 48)
    return t("premises:devicePresentation.duration.hours", { n: hours });
  return t("premises:devicePresentation.duration.days", {
    n: Math.floor(hours / 24),
  });
}

export default function InstallationCodesView({
  premiseId,
  onCreateClick,
  refreshTick,
}: Props) {
  const [codes, setCodes] = useState<PairingCode[]>([]);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();

  const refresh = useCallback(async () => {
    try {
      setCodes(await foyerApi.listPairings(premiseId));
      setError(null);
    } catch (err) {
      setError(errorText(err, t));
    }
  }, [premiseId, t]);

  useEffect(() => {
    refresh();
    const t = window.setInterval(refresh, 5_000);
    return () => window.clearInterval(t);
  }, [refresh, refreshTick]);

  async function revoke(code: string) {
    if (!confirm(t("installationCodesView.revokeConfirm", { code }))) return;
    try {
      await foyerApi.revokePairing(code);
      toast.success(t("installationCodesView.revoked", { code }));
    } catch (err) {
      toast.error(describeError(t, "revokeInstallationCode", err, { code }));
    }
    refresh();
  }

  function copyCode(code: string) {
    navigator.clipboard
      .writeText(code)
      .then(() => toast.success(t("installationCodesView.copied", { code })))
      .catch(() =>
        toast.error(t("installationCodesView.copyFailed", { code }))
      );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">{t("installationCodesView.title")}</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t("common:buttons.refresh")}>
            <IconButton
              onClick={refresh}
              aria-label={t("installationCodesView.refreshCodes")}
              data-testid="codes-refresh"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateClick}
            data-testid="codes-new"
          >
            {t("installationCodesView.newCode")}
          </Button>
        </Stack>
      </Stack>
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
      {codes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          <Trans
            t={t}
            i18nKey="installationCodesView.empty"
            components={{ strong: <strong /> }}
          />
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" data-testid="codes-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("installationCodesView.columns.code")}</TableCell>
                <TableCell>
                  {t("installationCodesView.columns.createdBy")}
                </TableCell>
                <TableCell>{t("common:labels.created")}</TableCell>
                <TableCell>
                  {t("installationCodesView.columns.expires")}
                </TableCell>
                <TableCell>{t("installationCodesView.columns.used")}</TableCell>
                <TableCell>{t("common:labels.status")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.map((p) => {
                const status = statusOf(p);
                return (
                  <TableRow key={p.code} hover>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography
                          sx={{
                            fontFamily: "fontFamilyMono",
                            fontWeight: "fontWeightSemibold",
                            letterSpacing: "0.08em",
                          }}
                        >
                          {p.code}
                        </Typography>
                        <Tooltip title={t("common:buttons.copy")}>
                          <IconButton
                            size="small"
                            aria-label={t("installationCodesView.copyCode")}
                            onClick={() => copyCode(p.code)}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {p.createdBy ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fmt.dateTime(p.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">
                          {fmt.dateTime(p.expiresAt)}
                        </Typography>
                        {status === "active" && (
                          <Typography variant="caption" color="text.secondary">
                            {t("installationCodesView.timeLeft", {
                              duration: timeLeft(t, p.expiresAt),
                            })}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {t("installationCodesView.usedCount", {
                          count: p.redemptions?.length ?? 0,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={status === "active" ? "success" : "default"}
                        variant={status === "active" ? "filled" : "outlined"}
                        label={t(`installationCodesView.status.${status}`)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {status === "active" && (
                        <Tooltip title={t("installationCodesView.revoke")}>
                          <IconButton
                            size="small"
                            aria-label={t("installationCodesView.revokeCode", {
                              code: p.code,
                            })}
                            onClick={() => revoke(p.code)}
                            data-testid={`code-revoke-${p.code}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
